type MatchType = "CONTAINS" | "EXACT" | "REGEX";

type VariantConfig = {
  id: string;
  name: string;
  weight: number;
  css: string;
  js: string;
};

type PageRuleConfig = {
  matchType: MatchType;
  pattern: string;
};

type GoalConfig = {
  id: string;
  key: string;
  type: "PAGEVIEW" | "CLICK" | "CUSTOM" | "REVENUE";
  config: Record<string, unknown>;
};

type AudienceConfig = {
  id: string;
  type: "ALL" | "DEVICE";
  config: Record<string, unknown>;
};

type ExperimentConfig = {
  id: string;
  name: string;
  trafficAllocation: number;
  antiFlicker: boolean;
  variants: VariantConfig[];
  pageRules: PageRuleConfig[];
  audiences: AudienceConfig[];
  goals: GoalConfig[];
};

type ProjectConfig = {
  projectId: string;
  experiments: ExperimentConfig[];
};

type BucketMap = Record<string, string>;

type ForceMap = Record<string, string>;

type AbClosApi = {
  track: (goalKey: string, data?: { value?: number }) => void;
  getVariant: (experimentId: string) => string | null;
  debug: boolean;
  preview: boolean;
  forced: ForceMap;
};

declare global {
  interface Window {
    abclos?: AbClosApi;
    __abclos_key?: string;
    __abclos_endpoint?: string;
    __abclos_debug?: boolean;
  }
}

const COOKIE_NAME = "abclos_exps";
const COOKIE_DAYS = 365;

function log(...args: unknown[]) {
  if (window.__abclos_debug || (window.abclos && window.abclos.debug)) {
    // eslint-disable-next-line no-console
    console.log("[AbClos]", ...args);
  }
}

function getScriptAttrs(): { key: string; endpoint: string; debug: boolean } {
  const scripts = document.getElementsByTagName("script");
  let key = window.__abclos_key || "";
  let endpoint = window.__abclos_endpoint || "";
  let debug = !!window.__abclos_debug;

  for (let i = 0; i < scripts.length; i++) {
    const s = scripts[i];
    const src = s.getAttribute("src") || "";
    if (src.indexOf("snippet") === -1 && !s.hasAttribute("data-abclos-key")) continue;
    const k = s.getAttribute("data-abclos-key");
    const e = s.getAttribute("data-abclos-endpoint");
    const d = s.getAttribute("data-abclos-debug");
    if (k) key = k;
    if (e) endpoint = e;
    if (d === "true" || d === "1") debug = true;
  }

  if (!endpoint) {
    const origin = (document.currentScript as HTMLScriptElement | null)?.src;
    if (origin) {
      try {
        endpoint = new URL(origin).origin;
      } catch {
        endpoint = "";
      }
    }
  }

  return { key, endpoint, debug };
}

function readCookie(): BucketMap {
  const match = document.cookie.match(new RegExp("(?:^|; )" + COOKIE_NAME + "=([^;]*)"));
  if (!match) return {};
  try {
    return JSON.parse(decodeURIComponent(match[1])) as BucketMap;
  } catch {
    return {};
  }
}

function writeCookie(map: BucketMap) {
  const expires = new Date(Date.now() + COOKIE_DAYS * 864e5).toUTCString();
  document.cookie =
    COOKIE_NAME +
    "=" +
    encodeURIComponent(JSON.stringify(map)) +
    "; expires=" +
    expires +
    "; path=/; SameSite=Lax";
}

function hashString(str: string): number {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function matchesRule(url: string, rule: PageRuleConfig): boolean {
  try {
    if (rule.matchType === "EXACT") return url === rule.pattern;
    if (rule.matchType === "CONTAINS") return url.indexOf(rule.pattern) !== -1;
    if (rule.matchType === "REGEX") return new RegExp(rule.pattern).test(url);
  } catch {
    return false;
  }
  return false;
}

function matchesTargeting(exp: ExperimentConfig, url: string): boolean {
  if (!exp.pageRules.length) return true;
  return exp.pageRules.some((r) => matchesRule(url, r));
}

/** Classe appareil : mobile | tablet | desktop */
function getDeviceClass(): "mobile" | "tablet" | "desktop" {
  const ua = navigator.userAgent || "";
  const iPadOS =
    /iPad/.test(ua) ||
    (navigator.platform === "MacIntel" && (navigator.maxTouchPoints || 0) > 1);
  const androidTablet = /Android/i.test(ua) && !/Mobile/i.test(ua);
  if (iPadOS || androidTablet || /Tablet|PlayBook|Silk/i.test(ua)) {
    return "tablet";
  }
  if (/Mobi|Android|iPhone|iPod/i.test(ua)) {
    return "mobile";
  }
  return "desktop";
}

function matchesAudience(audience: AudienceConfig): boolean {
  if (audience.type === "ALL") return true;
  if (audience.type === "DEVICE") {
    const wanted = String(audience.config.device || "mobile");
    return getDeviceClass() === wanted;
  }
  return false;
}

/** Aucune audience liée = tout le monde ; sinon AU MOINS une doit matcher */
function matchesAudiences(exp: ExperimentConfig): boolean {
  const list = exp.audiences || [];
  if (!list.length) return true;
  return list.some((a) => matchesAudience(a));
}

function pickVariant(exp: ExperimentConfig, visitorId: string): VariantConfig | null {
  const variants = exp.variants;
  if (!variants.length) return null;

  const totalWeight = variants.reduce((s, v) => s + (v.weight || 0), 0);
  if (totalWeight <= 0) return variants[0];

  const bucket = hashString(visitorId + ":" + exp.id) % 10000;
  const trafficCut = Math.floor((exp.trafficAllocation / 100) * 10000);
  if (bucket >= trafficCut) return null;

  const slot = hashString(visitorId + ":v:" + exp.id) % totalWeight;
  let acc = 0;
  for (let i = 0; i < variants.length; i++) {
    acc += variants[i].weight;
    if (slot < acc) return variants[i];
  }
  return variants[variants.length - 1];
}

function getVisitorId(): string {
  const map = readCookie();
  if (map._vid) return map._vid;
  const vid =
    "v_" +
    hashString(String(Date.now()) + Math.random().toString(36)).toString(36) +
    Math.random().toString(36).slice(2, 8);
  map._vid = vid;
  writeCookie(map);
  return vid;
}

/**
 * Query params:
 * - abclos_force=experimentId:variantId  (recommandé)
 * - abclos_force=variantId               (si unique dans la config)
 * - abclos_preview=1                     (pas d'envoi exposure/goals)
 * - abclos_pick=1                        (mode choix de sélecteur CSS)
 */
function parsePreviewParams(): {
  force: ForceMap;
  preview: boolean;
  pick: boolean;
  forceExperimentIds: string[];
} {
  const force: ForceMap = {};
  let preview = false;
  let pick = false;
  try {
    const params = new URLSearchParams(location.search);
    const previewRaw = params.get("abclos_preview");
    preview = previewRaw === "1" || previewRaw === "true";
    const pickRaw = params.get("abclos_pick");
    pick = pickRaw === "1" || pickRaw === "true";
    if (pick) preview = true;
    const raw = params.get("abclos_force");
    if (raw) {
      raw.split(",").forEach((part) => {
        const token = part.trim();
        if (!token) return;
        const colon = token.indexOf(":");
        if (colon > 0) {
          const expId = token.slice(0, colon).trim();
          const varId = token.slice(colon + 1).trim();
          if (expId && varId) force[expId] = varId;
        } else {
          // variantId seul — résolu plus tard quand on a la config
          force["*"] = token;
        }
      });
    }
  } catch {
    /* ignore */
  }
  return {
    force,
    preview,
    pick,
    forceExperimentIds: Object.keys(force).filter((k) => k !== "*"),
  };
}

function escapeCssIdent(s: string): string {
  if (typeof CSS !== "undefined" && typeof CSS.escape === "function") {
    return CSS.escape(s);
  }
  return s.replace(/([ !"#$%&'()*+,./:;<=>?@[\\\]^`{|}~])/g, "\\$1");
}

/** Sélecteur CSS raisonnable pour un élément (id > classes > chemin court). */
function cssPathFor(el: Element): string {
  if (el.id) return "#" + escapeCssIdent(el.id);
  const parts: string[] = [];
  let cur: Element | null = el;
  while (cur && cur.nodeType === 1 && cur !== document.documentElement) {
    let part = cur.tagName.toLowerCase();
    if (cur.classList && cur.classList.length) {
      const cls = Array.prototype.slice
        .call(cur.classList)
        .filter((c: string) => c && c.indexOf("abclos") !== 0)
        .slice(0, 2);
      if (cls.length) {
        part +=
          "." +
          cls
            .map((c: string) => escapeCssIdent(c))
            .join(".");
      }
    }
    const parent: Element | null = cur.parentElement;
    if (parent) {
      const siblings = Array.prototype.filter.call(parent.children, (c: Element) => {
        return c.tagName === cur!.tagName;
      }) as Element[];
      if (siblings.length > 1) {
        const idx = siblings.indexOf(cur) + 1;
        part += ":nth-of-type(" + idx + ")";
      }
    }
    parts.unshift(part);
    if (parts.length >= 4) break;
    cur = parent;
  }
  return parts.join(" > ");
}

function showPreviewBanner(lines: string[]) {
  if (document.getElementById("abclos-preview-banner")) return;
  const bar = document.createElement("div");
  bar.id = "abclos-preview-banner";
  bar.setAttribute("data-abclos", "banner");
  bar.style.cssText =
    "position:fixed;z-index:2147483646;left:0;right:0;bottom:0;padding:10px 16px;" +
    "background:#0f766e;color:#fff;font:600 13px/1.35 system-ui,sans-serif;" +
    "box-shadow:0 -4px 20px rgba(0,0,0,.15);display:flex;gap:12px;align-items:center;" +
    "justify-content:space-between;flex-wrap:wrap;";
  const text = document.createElement("div");
  text.textContent = "AbClos preview — " + lines.join(" · ");
  const close = document.createElement("button");
  close.type = "button";
  close.textContent = "Fermer";
  close.style.cssText =
    "background:rgba(255,255,255,.2);border:0;color:#fff;border-radius:6px;" +
    "padding:6px 10px;cursor:pointer;font:600 12px system-ui,sans-serif;";
  close.onclick = () => bar.remove();
  bar.appendChild(text);
  bar.appendChild(close);
  document.documentElement.appendChild(bar);
}

function startSelectorPicker() {
  if (document.getElementById("abclos-pick-style")) return;

  const style = document.createElement("style");
  style.id = "abclos-pick-style";
  style.textContent =
    ".abclos-pick-hover{outline:2px solid #0d9488!important;outline-offset:2px!important;cursor:crosshair!important;}" +
    "#abclos-pick-hint{position:fixed;z-index:2147483647;top:12px;left:50%;transform:translateX(-50%);" +
    "display:flex;align-items:center;gap:10px;flex-wrap:wrap;max-width:calc(100vw - 24px);" +
    "background:#111;color:#fff;padding:10px 14px;border-radius:8px;font:600 13px system-ui,sans-serif;" +
    "box-shadow:0 8px 24px rgba(0,0,0,.25);}" +
    "#abclos-pick-hint button{border:0;border-radius:6px;padding:6px 10px;cursor:pointer;" +
    "font:600 12px system-ui,sans-serif;}" +
    "#abclos-pick-hint button.abclos-pick-active{background:#0d9488;color:#fff;}" +
    "#abclos-pick-hint button:not(.abclos-pick-active){background:rgba(255,255,255,.15);color:#fff;}";
  document.head.appendChild(style);

  const hint = document.createElement("div");
  hint.id = "abclos-pick-hint";

  const label = document.createElement("span");
  label.textContent = "AbClos — naviguez (cookies…), puis sélectionnez";

  const btnNav = document.createElement("button");
  btnNav.type = "button";
  btnNav.textContent = "Naviguer";

  const btnPick = document.createElement("button");
  btnPick.type = "button";
  btnPick.textContent = "Sélectionner";

  hint.appendChild(label);
  hint.appendChild(btnNav);
  hint.appendChild(btnPick);
  document.documentElement.appendChild(hint);

  /** Défaut : naviguer (Axeptio, liens). Sélectionner = capture du sélecteur. */
  let picking = false;
  let last: Element | null = null;

  function clearHover() {
    if (last) last.classList.remove("abclos-pick-hover");
    last = null;
  }

  function setMode(next: boolean) {
    picking = next;
    btnNav.classList.toggle("abclos-pick-active", !picking);
    btnPick.classList.toggle("abclos-pick-active", picking);
    label.textContent = picking
      ? "AbClos — cliquez un élément pour copier son sélecteur"
      : "AbClos — naviguez (cookies…), puis sélectionnez";
    if (!picking) clearHover();
    document.documentElement.style.cursor = picking ? "crosshair" : "";
  }

  function isUiTarget(t: Element | null): boolean {
    return !!(t && (t.id === "abclos-pick-hint" || t.closest("#abclos-pick-hint") || t.closest("#abclos-preview-banner")));
  }

  function onMove(e: MouseEvent) {
    if (!picking) return;
    const t = e.target as Element | null;
    if (!t || t === last || isUiTarget(t)) return;
    if (last) last.classList.remove("abclos-pick-hover");
    last = t;
    t.classList.add("abclos-pick-hover");
  }

  function cleanup() {
    document.removeEventListener("mousemove", onMove, true);
    document.removeEventListener("click", onClick, true);
    clearHover();
    document.documentElement.style.cursor = "";
    hint.remove();
    style.remove();
  }

  function onClick(e: MouseEvent) {
    const t = e.target as Element | null;
    if (isUiTarget(t)) return;
    if (!picking) return;
    e.preventDefault();
    e.stopPropagation();
    if (!t) return;
    const selector = cssPathFor(t);
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(selector);
      }
    } catch {
      /* ignore */
    }
    try {
      if (window.opener && !window.opener.closed) {
        window.opener.postMessage({ source: "abclos-pick", selector }, "*");
      }
    } catch {
      /* ignore */
    }
    label.textContent = "Sélecteur envoyé : " + selector;
    btnNav.remove();
    btnPick.remove();
    cleanup();
    setTimeout(() => {
      try {
        window.close();
      } catch {
        /* ignore */
      }
    }, 600);
  }

  btnNav.onclick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setMode(false);
  };
  btnPick.onclick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setMode(true);
  };

  document.addEventListener("mousemove", onMove, true);
  document.addEventListener("click", onClick, true);
  setMode(false);
}

function resolveForceMap(force: ForceMap, experiments: ExperimentConfig[]): ForceMap {
  const resolved: ForceMap = {};
  Object.keys(force).forEach((expId) => {
    if (expId === "*") return;
    resolved[expId] = force[expId];
  });
  const loneVariantId = force["*"];
  if (loneVariantId) {
    experiments.forEach((exp) => {
      if (resolved[exp.id]) return;
      if (exp.variants.some((v) => v.id === loneVariantId)) {
        resolved[exp.id] = loneVariantId;
      }
    });
  }
  return resolved;
}

function applyCss(css: string, expId: string) {
  if (!css) return;
  const id = "abclos-css-" + expId;
  if (document.getElementById(id)) return;
  const style = document.createElement("style");
  style.id = id;
  style.textContent = css;
  document.head.appendChild(style);
}

function applyJs(js: string) {
  if (!js) return;
  try {
    // Variante code-first : exécution volontaire du JS configuré dans le dashboard
    const fn = new Function(js);
    fn();
  } catch (err) {
    log("Erreur JS variante", err);
  }
}

function trackedKey(eventType: string, experimentId: string, goalKey?: string) {
  return eventType === "goal"
    ? "_g:" + experimentId + ":" + (goalKey || "")
    : "_e:" + experimentId;
}

function hasTracked(map: BucketMap, eventType: string, experimentId: string, goalKey?: string) {
  return !!map[trackedKey(eventType, experimentId, goalKey)];
}

function markTracked(map: BucketMap, eventType: string, experimentId: string, goalKey?: string) {
  map[trackedKey(eventType, experimentId, goalKey)] = "1";
  writeCookie(map);
}

function sendTrack(
  endpoint: string,
  key: string,
  visitorId: string,
  payload: Record<string, unknown>
) {
  const body = JSON.stringify({ key, visitorId, ...payload });
  const url = endpoint.replace(/\/$/, "") + "/api/v1/track";
  // Pas de sendBeacon + application/json en cross-origin :
  // le navigateur peut renvoyer true puis échouer (ERR_FAILED) sans fallback.
  fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body,
    keepalive: true,
    mode: "cors",
    credentials: "omit",
  }).catch(() => undefined);
}

function bindClickGoals(
  goals: GoalConfig[],
  onHit: (goalKey: string, value?: number) => void
) {
  goals
    .filter((g) => g.type === "CLICK" && g.config && g.config.selector)
    .forEach((g) => {
      const selector = String(g.config.selector);
      document.addEventListener(
        "click",
        (ev) => {
          const t = ev.target as Element | null;
          if (!t) return;
          const el = t.closest ? t.closest(selector) : null;
          if (el) onHit(g.key);
        },
        true
      );
    });
}

function bindPageviewGoals(
  goals: GoalConfig[],
  url: string,
  onHit: (goalKey: string) => void
) {
  goals
    .filter((g) => g.type === "PAGEVIEW")
    .forEach((g) => {
      const pattern = g.config && g.config.urlPattern ? String(g.config.urlPattern) : "";
      if (!pattern || url.indexOf(pattern) !== -1) onHit(g.key);
    });
}

async function boot() {
  const { key, endpoint, debug } = getScriptAttrs();
  const { force: forceRaw, preview, pick, forceExperimentIds } = parsePreviewParams();
  const assignments: BucketMap = readCookie();
  const activeAssignments: Record<string, string> = {};
  const previewLabels: string[] = [];

  const visitorId = getVisitorId();

  const api: AbClosApi = {
    debug,
    preview,
    forced: {},
    track: (goalKey, data) => {
      if (!key || !endpoint) return;
      if (preview) {
        log("track ignoré (preview)", goalKey, data);
        return;
      }
      const entries = Object.keys(activeAssignments);
      if (!entries.length) return;
      const value = data && typeof data.value === "number" ? data.value : 0;
      const cookieMap = readCookie();
      entries.forEach((experimentId) => {
        const variantId = activeAssignments[experimentId];
        if (!variantId) return;
        // Revenu avec montant : toujours envoyer (serveur cumule valueSum)
        const isRevenueHit = value > 0;
        if (!isRevenueHit && hasTracked(cookieMap, "goal", experimentId, goalKey)) {
          log("goal déjà compté", goalKey, experimentId);
          return;
        }
        if (!isRevenueHit) {
          markTracked(cookieMap, "goal", experimentId, goalKey);
        }
        sendTrack(endpoint, key, visitorId, {
          type: "goal",
          experimentId,
          variantId,
          goalKey,
          value,
        });
      });
      log("track", goalKey, data);
    },
    getVariant: (experimentId) => activeAssignments[experimentId] || null,
  };
  window.abclos = api;

  if (pick) {
    // Mode choix de sélecteur : DOM original, pas de CSS/JS de variation.
    // Retirer l'anti-flicker tout de suite (sinon la pastille est en opacity:0).
    document.documentElement.classList.remove("abclos-hide");
    startSelectorPicker();
    log("mode pick actif");
  }

  if (!key || !endpoint) {
    log("Clé ou endpoint manquant");
    return;
  }

  const url = location.href;

  let config: ProjectConfig;
  try {
    let configUrl =
      endpoint.replace(/\/$/, "") + "/api/v1/config?key=" + encodeURIComponent(key);
    forceExperimentIds.forEach((id) => {
      configUrl += "&previewExperimentId=" + encodeURIComponent(id);
    });
    const res = await fetch(configUrl, { credentials: "omit", mode: "cors" });
    if (!res.ok) throw new Error("config " + res.status);
    config = (await res.json()) as ProjectConfig;
  } catch (err) {
    log("Impossible de charger la config", err);
    document.documentElement.classList.remove("abclos-hide");
    return;
  }

  if (pick) {
    document.documentElement.classList.remove("abclos-hide");
    return;
  }

  const experiments = config.experiments || [];
  const forceMap = resolveForceMap(forceRaw, experiments);
  api.forced = forceMap;
  let anyAntiFlicker = false;

  experiments.forEach((exp) => {
    const forcedVariantId = forceMap[exp.id];
    const isForced = !!forcedVariantId;

    if (!isForced && !matchesAudiences(exp)) {
      log("skip audience", exp.name);
      return;
    }

    if (!isForced && !matchesTargeting(exp, url)) {
      log("skip targeting", exp.name);
      return;
    }

    if (exp.antiFlicker) {
      anyAntiFlicker = true;
      document.documentElement.classList.add("abclos-hide");
    }

    let variant: VariantConfig | null = null;

    if (isForced) {
      variant = exp.variants.find((v) => v.id === forcedVariantId) || null;
      if (!variant) {
        log("force: variante introuvable", exp.name, forcedVariantId);
        return;
      }
      assignments[exp.id] = variant.id;
      writeCookie(assignments);
      log("force", exp.name, variant.name);
    } else {
      let variantId = assignments[exp.id];
      variant = exp.variants.find((v) => v.id === variantId) || null;

      if (!variant) {
        variant = pickVariant(exp, visitorId);
        if (variant) {
          assignments[exp.id] = variant.id;
          writeCookie(assignments);
        }
      }

      if (!variant) {
        log("hors allocation", exp.name);
        return;
      }
    }

    activeAssignments[exp.id] = variant.id;
    applyCss(variant.css, exp.id);
    applyJs(variant.js);

    if (preview) {
      previewLabels.push(exp.name + " → " + variant.name);
    }

    if (!preview) {
      const cookieMap = readCookie();
      if (hasTracked(cookieMap, "exposure", exp.id)) {
        log("exposure déjà comptée", exp.name);
      } else {
        markTracked(cookieMap, "exposure", exp.id);
        sendTrack(endpoint, key, visitorId, {
          type: "exposure",
          experimentId: exp.id,
          variantId: variant.id,
        });
      }
    } else {
      log("exposure ignorée (preview)", exp.name);
    }

    bindClickGoals(exp.goals, (goalKey) => api.track(goalKey));
    bindPageviewGoals(exp.goals, url, (goalKey) => api.track(goalKey));

    log("active", exp.name, variant.name, preview ? "(preview)" : "");
  });

  if (preview && previewLabels.length) {
    showPreviewBanner(previewLabels);
  }

  if (anyAntiFlicker || document.documentElement.classList.contains("abclos-hide")) {
    requestAnimationFrame(() => {
      document.documentElement.classList.remove("abclos-hide");
    });
  }
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    boot();
  });
} else {
  boot();
}

export {};
